import type {
  MediaRecorderTranscriptTurn,
  MediaTranscriberEngine,
  MediaTranscriberEventHandlers,
} from "@/store/audio/recording/media-types";
import type { DesktopRecorderTranscriptTurn } from "@/store/audio/recording/media-transcriber/transcriber.types";

export class DesktopMediaTranscriber implements MediaTranscriberEngine {
  private handlers: MediaTranscriberEventHandlers = {};
  private readonly emitError: (error: unknown) => void;

  constructor({ emitError }: { emitError: (error: unknown) => void }) {
    this.emitError = emitError;
  }

  setEventHandlers(handlers: MediaTranscriberEventHandlers): void {
    this.handlers = handlers ?? {};
  }

  async start(): Promise<void> {
    this.handlers.onStart?.();
  }

  async stop(): Promise<void> {
    this.handlers.onStop?.();
  }

  async pause(): Promise<void> {
    // Desktop transcripts are fed in via the `desktop-media-recorder`.
    // Pausing happens there instead.
  }

  async resume(): Promise<void> {
    // Desktop transcripts are fed in via the `desktop-media-recorder`.
    // Resuming happens there instead.
  }

  dispose(): void {
    void this.stop();
  }

  handleTranscript({
    channel,
    turn,
  }: {
    channel: 0 | 1;
    turn: DesktopRecorderTranscriptTurn;
  }): void {
    try {
      const mapped = this.mapTranscriptTurn({ channel, turn });
      this.handlers.onTranscriptTurn?.({ turn: mapped });
    } catch (error) {
      this.emitError(error);
    }
  }

  private mapTranscriptTurn({
    channel,
    turn,
  }: {
    channel: 0 | 1;
    turn: DesktopRecorderTranscriptTurn;
  }): MediaRecorderTranscriptTurn {
    return {
      channel,
      transcript: turn.transcript,
      timestampMs: turn.timestampMs,
      turnOrder: turn.turnOrder,
      words: (turn.words ?? []).map(word => ({
        word: word.text,
        startMs: word.startMs,
        endMs: word.endMs,
      })),
      raw: turn.raw ?? turn,
    };
  }
}
